'use client';

import { handleError } from '@lib/util/handle-error';
import { Region } from '@medusajs/medusa';
import { getLogger } from '@lib/util/logging-util';
import {
    useCart,
    useCreateLineItem,
    useDeleteLineItem,
    useUpdateLineItem,
} from 'medusa-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useCartDropdown } from './cart-dropdown-context';
import { useSearchParams, useRouter } from 'next/navigation';
import { LineItemMetadata } from '../../types/lineItemMetadata';
import { AxiosError } from 'axios';
import RouterLoading from '@modules/common/components/router-loading';
import { toast } from 'react-toastify';
import Link from 'next/link';
import { toaster } from '@lib/util/toaster';
import { gaCartItem, trackAddToCart, trackRemoveFromCart } from '@lib/util/google-analytics-utils';
import { errors } from '@lib/constants/network-constants';
import { CARD_PROGRAMS, DEFAULT_SEARCH_PROVIDER } from '@lib/constants';
import { CURRENCY } from '@lib/constants/variants';
import { AffiliateLandingInterface } from 'types/affiliate';
import { useMutation } from "@tanstack/react-query"
import { reApplyBonusCard } from '@lib/data/bonus-card-handle';
import { getAwc, getReferral } from '@lib/util/local-storage';
import { useApi } from '@lib/hooks/use-api';

interface VariantInfoProps {
    variantId: string;
    quantity: number;
    metadata?: any;
}

interface LineInfoProps {
    lineId: string;
    quantity: number;
}

interface StoreContext {
    countryCode: string | undefined;
    setRegion: (regionId: string, countryCode: string) => void;
    addItem: (item: VariantInfoProps) => void;
    updateItem: (item: LineInfoProps) => void;
    deleteItem: (lineId: string, hasLoading?: boolean, itemRemove?: any) => void;
    resetCart: () => void;
    setLoading: (value: boolean) => void;
    loading: boolean;
    setBuyNow: (value: boolean) => void;
    isDeleteItemFromCart: boolean;
    setIsDeleteItemFromCart: (value: boolean) => void;
    checkoutEmail: string;
    setCheckoutEmail: (value: string) => void;
    ref: AffiliateLandingInterface | any,
    setRef: (value: any) => void;
    requiredEmailToApplyPromoCode: boolean;
    setRequiredEmailToApplyPromoCode: (value: boolean) => void;
}

const StoreContext = React.createContext<StoreContext | null>(null);

export const useStore = () => {
    const context = React.useContext(StoreContext);
    if (context === null) {
        throw new Error('useStore must be used within a StoreProvider');
    }
    return context;
};

interface StoreProps {
    children: React.ReactNode;
}

const IS_SERVER = typeof window === 'undefined';
const CART_KEY = 'medusa_cart_id';
const REGION_KEY = 'medusa_region';

export const StoreProvider = ({ children }: StoreProps) => {
    const logger = getLogger();
    const router = useRouter();
    const api = useApi();
    const { cart, setCart, createCart, updateCart } = useCart();
    const { mutate: removeDiscount } = useMutation(
        (payload: { cartId: string; code: string }) => {
            return api.getMedusaClient().carts.deleteDiscount(payload.cartId, payload.code)
        }
    )
    const [countryCode, setCountryCode] = useState<string | undefined>(
        undefined
    );
    const [loading, setLoading] = useState<boolean>(false);
    const { timedOpen } = useCartDropdown();
    const addLineItem = useCreateLineItem(cart?.id!);
    const removeLineItem = useDeleteLineItem(cart?.id!);
    const adjustLineItem = useUpdateLineItem(cart?.id!);
    const [buyNow, setBuyNow] = useState<boolean>(false);
    const [ref, setRef] = useState<AffiliateLandingInterface>();
    const [requiredEmailToApplyPromoCode, setRequiredEmailToApplyPromoCode] = useState<boolean>(false);

    const [isDeleteItemFromCart, setIsDeleteItemFromCart] =
        useState<boolean>(false);
    const [checkoutEmail, setCheckoutEmail] = useState<string>('');

    // check if the user is onboarding and sets the onboarding session storage
    const searchParams = useSearchParams();
    const onboardingCartId = searchParams.get('cart_id');
    const isOnboarding = searchParams.get('onboarding');

    useEffect(() => {
        if (isOnboarding === 'true') {
            sessionStorage.setItem('onboarding', 'true');
        }
    }, [isOnboarding]);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        if (loading) {
            timeoutId = setTimeout(() => {
                if (loading) toast.error('Operation Timed Out');
                setLoading(false);
            }, 60000);
        }

        return () => {
            clearTimeout(timeoutId);
        };
    }, [loading]);

    const storeRegion = (regionId: string, countryCode: string) => {
        if (!IS_SERVER) {
            localStorage.setItem(
                REGION_KEY,
                JSON.stringify({ regionId, countryCode })
            );

            setCountryCode(countryCode);
        }
    };

    useEffect(() => {
        if (!IS_SERVER) {
            const storedRegion = localStorage.getItem(REGION_KEY);
            if (storedRegion) {
                const { countryCode } = JSON.parse(storedRegion);
                setCountryCode(countryCode);
            }
        }
    }, []);

    const getRegion = () => {
        if (!IS_SERVER) {
            const region = localStorage.getItem(REGION_KEY);
            if (region) {
                return JSON.parse(region) as {
                    regionId: string;
                    countryCode: string;
                };
            }
        }
        return null;
    };

    const setRegion = async (regionId: string, countryCode: string) => {
        await updateCart.mutateAsync(
            {
                region_id: regionId,
            },
            {
                onSuccess: ({ cart }) => {
                    setCart(cart);
                    storeCart(cart.id);
                    storeRegion(regionId, countryCode);
                },
                onError: (error) => {
                    if (process.env.NODE_ENV === 'development') {
                        logger.error(error);
                    }
                },
            }
        );
    };

    const ensureRegion = (region: Region, countryCode?: string | null) => {
        if (!IS_SERVER) {
            const { regionId, countryCode: defaultCountryCode } =
                getRegion() || {
                    regionId: region.id,
                    countryCode: region.countries[0].iso_2,
                };

            const finalCountryCode = countryCode || defaultCountryCode;

            if (regionId !== region.id) {
                setRegion(region.id, finalCountryCode);
            }

            storeRegion(region.id, finalCountryCode);
            setCountryCode(finalCountryCode);
        }
    };

    const storeCart = (id: string) => {
        if (!IS_SERVER) {
            localStorage.setItem(CART_KEY, id);
        }
    };

    const getCart = () => {
        if (!IS_SERVER) {
            return localStorage.getItem(CART_KEY);
        }
        return null;
    };

    const deleteCart = () => {
        if (!IS_SERVER) {
            localStorage.removeItem(CART_KEY);
        }
    };

    const deleteRegion = () => {
        if (!IS_SERVER) {
            localStorage.removeItem(REGION_KEY);
        }
    };

    const createNewCart = async (regionId?: string) => {
        try {
            await createCart.mutateAsync(
                { region_id: regionId },
                {
                    onSuccess: ({ cart }) => {
                        setCart(cart);
                        storeCart(cart.id);
                        ensureRegion(
                            cart.region,
                            cart.shipping_address?.country_code
                        );
                    },
                    onError: (error) => {
                        if (process.env.NODE_ENV === 'development') {
                            logger.error(error);
                        }
                    },
                }
            );
        } catch { }
    };

    const resetCart = () => {
        deleteCart();

        const savedRegion = getRegion();

        createCart.mutate(
            {
                region_id: savedRegion?.regionId,
            },
            {
                onSuccess: ({ cart }) => {
                    setCart(cart);
                    storeCart(cart.id);
                    ensureRegion(
                        cart.region,
                        cart.shipping_address?.country_code
                    );
                },
                onError: (error) => {
                    if (process.env.NODE_ENV === 'development') {
                        logger.error(error);
                    }
                },
            }
        );
    };

    useEffect(() => {
        const ensureCart = async () => {
            const cartId = onboardingCartId || getCart();
            const region = getRegion();

            if (cartId) {
                const cartRes = await api.getMedusaClient().carts
                    .retrieve(cartId)
                    .then(({ cart }) => {
                        return cart;
                    })
                    .catch(async (_) => {
                        return null;
                    });

                if (!cartRes || cartRes.completed_at) {
                    deleteCart();
                    deleteRegion();
                    await createNewCart();
                    return;
                }

                setCart(cartRes);
                ensureRegion(cartRes.region);
            } else {
                await createNewCart(region?.regionId);
            }
        };

        if (!IS_SERVER && !cart?.id) {
            ensureCart();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleReApplyBonusCard = async (cart: any) => {
        if (!cart.bonus_code) return;
        await reApplyBonusCard(cart.id);
    };

    const addItem = ({
        variantId,
        quantity,
        metadata,
    }: {
        variantId: string;
        quantity: number;
        metadata?: LineItemMetadata;
    }) => {
        if (variantId === 'enter') {
            return toaster.error('Please enter correct a amount.');
        }
        const lineItem = generateLineItem(variantId, quantity, metadata);

        addLineItem.mutate(lineItem,
            {
                onSuccess: ({ cart }) => {
                    setCart(cart);
                    storeCart(cart.id);
                    timedOpen();
                    handleReApplyBonusCard(cart);
                    const lineItemsPayload: gaCartItem = {
                        value: (metadata?.price ?? 0) / 100,
                        currency: CURRENCY,
                        items: [{
                            item_name: metadata?.business_name || '',
                            price: (metadata?.price ?? 0) / 100
                        }]
                    };
                    trackAddToCart(lineItemsPayload);
                    if (buyNow) {
                        router.push('/checkout');
                    };
                    setBuyNow(false);
                },
                onError: (error) => {
                    handleError(error);
                    const message =
                        ((error as AxiosError)?.response?.data as string) ||
                        errors.generic_axios_error;
                    toaster.error(
                        <div>
                            {message}, or{' '}
                            <Link
                                className="text-optimus_prime"
                                href="/contact-us"
                                prefetch={false}
                            >
                                contact us
                            </Link>
                            .
                        </div>
                    );
                    setLoading(false);
                    setBuyNow(false);
                },
            }
        );
    };

    const generateLineItem = (variantId: string, quantity: number, metadata?: LineItemMetadata) => {

        return {
            variant_id: variantId,
            quantity: quantity,
            metadata: {
                variant_id: metadata?.variant_id,
                relationship: metadata?.relationship || '',
                occasion: metadata?.occasion || '',
                business_external_id: metadata?.business_external_id ?? sessionStorage.getItem(CARD_PROGRAMS.KEY),
                business_name: metadata?.business_name,
                business_alias: metadata?.business_alias,
                photo_img_url: metadata?.photo_img_url,
                rating: metadata?.rating,
                review_count: metadata?.review_count,
                price: metadata?.price,
                url: metadata?.url,
                delivery_date: metadata?.delivery_date,
                city: metadata?.city,
                parent_category: metadata?.parent_category,
                card_program: getCardProgram(metadata?.card_program),
                ref: getReferral(),
                awc: getAwc(),
                search_provider: metadata?.search_provider ?? DEFAULT_SEARCH_PROVIDER,
                delivery_date_tz_offset: new Date().getTimezoneOffset(),
                author_display_name: metadata?.author_display_name,
                created_at: new Date().getTime(),
            },
        };
    };

    const getCardProgram = (card_program: string | undefined | null): string => {
        if(card_program) return card_program;
        return sessionStorage.getItem(CARD_PROGRAMS.KEY) || CARD_PROGRAMS.CHOOSE_YOUR_OWN;
    };

    const deleteItem = (lineId: string, hasLoading?: boolean, itemRemove?: any) => {
        // Track analytics if needed
        if (itemRemove) {
            trackRemoveFromCart({
                value: (itemRemove?.total ?? 0) / 100,
                currency: CURRENCY,
                items: [
                    {
                        item_name: `${(itemRemove?.total ?? 0) / 100} ${
                            itemRemove?.metadata?.business_name
                        } Gift Card`,
                        price: (itemRemove?.total ?? 0) / 100,
                    },
                ],
            });
        }

        // Set loading state if needed
        if (hasLoading) {
            setLoading(true);
        }

        // Handle navigation based on hasLoading parameter
        const handleRouteToRoot = () => {
            if (!hasLoading) {
                router.push('/');
            }
        };

        // Execute the mutation
        removeLineItem.mutate(
            { lineId },
            {
                onSuccess: ({ cart }) => {
                    setIsDeleteItemFromCart(true);

                    if (cart?.items?.length === 0 && cart?.discounts?.length > 0) {
                        removeDiscount(
                            { cartId: cart?.id, code: cart?.discounts[0].code },
                            {
                                onSuccess: ({ cart: updatedCart }) => {
                                    setCart(updatedCart);
                                    storeCart(updatedCart.id);
                                    setLoading(false);
                                    handleRouteToRoot();
                                },
                            }
                        );
                    } else {
                        setTimeout(() => {
                            setCart(cart);
                            storeCart(cart.id);
                            setLoading(false);
                            handleRouteToRoot();
                        }, 100);
                    }
                },
                onError: (error) => {
                    setLoading(false);
                    handleError(error);
                },
            }
        );
    };

    const updateItem = ({
        lineId,
        quantity,
    }: {
        lineId: string;
        quantity: number;
    }) => {
        adjustLineItem.mutate(
            {
                lineId,
                quantity,
            },
            {
                onSuccess: ({ cart }) => {
                    setCart(cart);
                    storeCart(cart.id);
                },
                onError: (error) => {
                    handleError(error);
                    const message = (
                        (error as AxiosError)?.response?.data as string[]
                    ).join('\r');
                    toast.error(message, {
                        position: 'top-right',
                        autoClose: 5000,
                        hideProgressBar: true,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: 'light',
                    });
                },
            }
        );
    };

    const contextValue = useMemo(
        () => ({
            countryCode,
            setRegion,
            addItem,
            deleteItem,
            updateItem,
            resetCart,
            setLoading,
            loading,
            setBuyNow,
            isDeleteItemFromCart,
            setIsDeleteItemFromCart,
            checkoutEmail,
            setCheckoutEmail,
            ref,
            setRef,
            requiredEmailToApplyPromoCode,
            setRequiredEmailToApplyPromoCode
        }),
        [countryCode, loading, ref, checkoutEmail, requiredEmailToApplyPromoCode]
    );

    return (
        <StoreContext.Provider value={contextValue}>
            {children}
            <RouterLoading loading={loading} />
        </StoreContext.Provider>
    );
};
